import {
  Button,
  ButtonGroup,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Label,
  showNotification,
} from 'platform/components';
import {HStack, VStack} from 'platform/foundation';

import {useGetServiceOrderQuery, usePostOrderActionMutation} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError, PredefinedNotes} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {
  CancelOrderWithReasonFormSchema,
  CancelOrderWithReasonFormType,
} from '../types/CancelOrderWithReasonFormType';

interface CancelOrderWithReasonDialogProps extends RequiredTestIdProps {
  serviceOrderId: string;
  serviceCaseId: string;
  actionKey: 'cancel' | 'cancel-claim';
  onClose: () => void;
}

export function CancelOrderWithReasonDialog(props: CancelOrderWithReasonDialogProps) {
  const {
    data: serviceOrder,
    isLoading,
    isError,
  } = useGetServiceOrderQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
  });

  const [postOrderAction] = usePostOrderActionMutation();

  const handleOrderCancel: FormSubmitHandler<CancelOrderWithReasonFormType> = (values) =>
    postOrderAction({
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
      actionKey: props.actionKey,
      body: {
        cancelReason: values.reason,
      },
    })
      .unwrap()
      .then(() => showNotification.success(i18n.t('entity.order.notifications.success-cancel')))
      .then(props.onClose)
      .catch(handleApiError);

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <Form<CancelOrderWithReasonFormType>
        defaultValues={{reason: serviceOrder?.cancelReason ?? undefined}}
        experimentalZodSchema={CancelOrderWithReasonFormSchema}
        onSubmit={handleOrderCancel}
      >
        {(control, formApi) => (
          <VStack spacing={4}>
            <VStack spacing={1}>
              <HStack justify="space-between" align="flex-end">
                <Label>{`* ${i18n.t('general.labels.reason')}`}</Label>
                <PredefinedNotes
                  data-testid={suffixTestId('cancel', props)}
                  note={formApi.watch('reason') ?? null}
                  onPrefill={(note) => formApi.setValue('reason', note)}
                  context="cancel_reason"
                  resource="SERVICE_ORDER"
                  isLinkVariant
                />
              </HStack>
              <FormField control={control} isRequired name="reason" type="text" />
            </VStack>

            <ButtonGroup align="right">
              <Button
                variant="secondary"
                onClick={props.onClose}
                title={i18n.t('general.actions.cancel')}
              />
              <FormButton
                control={control}
                type="submit"
                title={i18n.t('general.actions.confirm')}
              />
            </ButtonGroup>
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}
